import { BaseWebComponentController, IListenerConfig, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { templateWebAccessControlCustomPages } from '../view/template';
import { WebAccessControlCustomPagesComponent } from '../component/WebAccessControlCustomPagesComponent';
import { WebAccessControlCustomPagesView } from '../view/WebAccessControlCustomPagesView';
import { WebAccessControlCustomPagesRepository } from '../repository/WebAccessControlCustomPagesRepository';
import { GridEvents } from '../../../grid/types/common';
import { Rights } from '@nirvana/shared/constants';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { WacPagesGridEvents, WacPagesRecord, WebAccessControlCustomPagesGrid } from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { showErrorToast, showSuccessToast } from '../../../ui/gzToastNotification';
import { InlineMenuEvents } from '../../../mixins/InlineMenuMixin';
import { WebAccessControlCustomPagesFormView } from '../../webAccessControlCustomPagesForm/view/WebAccessControlCustomPagesFormView';

export class WebAccessControlCustomPagesController extends BaseWebComponentController {
  public component: WebAccessControlCustomPagesComponent;
  public view: WebAccessControlCustomPagesView;
  public dataRepository: WebAccessControlCustomPagesRepository;
  public viewConstructor: typeof WebAccessControlCustomPagesView;
  public dataRepositoryConstructor: typeof WebAccessControlCustomPagesRepository;
  private selectedGridRecords: Array<WacPagesRecord> = [];

  constructor(component: WebAccessControlCustomPagesComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: WebAccessControlCustomPagesView,
      viewTemplate: templateWebAccessControlCustomPages,
      dataRepositoryConstructor: WebAccessControlCustomPagesRepository,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$webAccessControlCustomPagesForm,
        event: WacPagesGridEvents.SAVE_PAGE,
        callback: this.refreshGrid,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: this.onSelectionChanged,
      },
      {
        element: this.view.dom.$grid,
        event: WacPagesGridEvents.CLICK_ON_PAGE_NAME,
        callback: this.onPageNameClick,
      },
      {
        element: this.view.dom.$grid,
        event: WacPagesGridEvents.CLICK_ON_IN_POLICIES,
        callback: this.onInPoliciesClick,
      },
      {
        element: this.view.dom.$addPageBtn,
        event: 'click',
        callback: this.onAddPageClick,
      },
      {
        element: this.view.dom.$deleteBtn,
        event: 'click',
        callback: this.onDeletePageClick,
      },
      {
        element: this.view.dom.$grid,
        event: WacPagesGridEvents.DELETE_PAGE,
        callback: this.onDeleteGridItemClick,
      },
      {
        element: this.view.dom.$grid,
        event: WacPagesGridEvents.EDIT_ASSIGNMENTS,
        callback: this.onEditPageAssignmentsClick,
      },
      {
        element: this.view.dom.$grid,
        event: InlineMenuEvents.ACTIVATE,
        callback: this.onInlineMenuClick,
      },
    ];
  }

  private onSelectionChanged(event: WebComponentEvent) {

    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.view.dom.$deleteBtn.disabled = true;
      return;
    }

    this.selectedGridRecords = event.detail.selectedItemsData;
    let disabled = true;

    if (!isEmpty(this.selectedGridRecords) && Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      disabled = false;
    }

    for (const record of this.selectedGridRecords) {
      if (!record.allowChangeByOtherUsers && record.created?.userId !== Application.Main.getUser()._id) {
        disabled = true;
        break;
      }
    }

    this.view.dom.$deleteBtn.disabled = disabled;
  }

  private refreshGrid() {
    this.view.dom.$grid.controller?.handleGridItemsLoad();
  }

  private onPageNameClick(event: WebComponentEvent) {
    this.view.showWebAccessControlCustomPagesForm(event.detail.rowData.item);
  }

  private onInPoliciesClick(event: WebComponentEvent) {
  }

  private onAddPageClick(event: WebComponentEvent) {
    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      return;
    }
    this.view.showWebAccessControlCustomPagesForm();
  }

  private onDeletePageClick(event: WebComponentEvent) {
    const windowTitle = translateFn('Delete selected pages');
    const windowBody = translateFn('Delete the selected pages?');
    const windowFooter = this.getDeleteConfirmationDialogFooterHtml();
    GzDialogWebComponent.computeCustomDialog(windowBody, windowFooter, windowTitle, null, this.onDeleteButtonConfirmationDialogConfirm.bind(this, this.selectedGridRecords));
  }

  private onDeleteGridItemClick(event: WebComponentEvent) {
    const record = [event.detail.rowData.item];
    const windowTitle = translateFn('Delete selected page');
    const windowBody = translateFn('Delete the selected page?');
    const windowFooter = this.getDeleteConfirmationDialogFooterHtml();
    GzDialogWebComponent.computeCustomDialog(windowBody, windowFooter, windowTitle, null, this.onDeleteButtonConfirmationDialogConfirm.bind(this, record));
  }

  private onEditPageAssignmentsClick(event: WebComponentEvent) {
  }

  public getDeleteConfirmationDialogFooterHtml(): string {
    return `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>${translateFn('DELETE')}</gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>${translateFn('CANCEL')}</gz-button>`;
  }

  private async onDeleteButtonConfirmationDialogConfirm(records: Array<WacPagesRecord>) {
    const unknownErrorMessage = translateFn('Failed to delete the pages. Please retry.');
    let removeError = null;
    try {
      const response = await this.dataRepository.removeRecords(records);

      if (!response) {
        removeError = unknownErrorMessage;
      }
    } catch (error) {
      removeError = error.message || unknownErrorMessage;
    }

    if (removeError) {
      showErrorToast(removeError);
      return;
    }

    showSuccessToast(translateFn('Pages deleted successfully'));
    this.refreshGrid();
  }

  private onInlineMenuClick(event: WebComponentEvent) {
    const hasPermissions = WebAccessControlCustomPagesFormView.hasCurrentUserPermissions(event.detail.itemData.item);
    (event.target as WebAccessControlCustomPagesGrid).inlineRowActions?.forEach(item => {
      if (item.value === WacPagesGridEvents.DELETE_PAGE) {
        item.disabled = !hasPermissions;
      }
    });
  }

}
